import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import { merge, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap
} from 'rxjs/operators';
import {
  PropertySearcherSearchBean,
  PropertySearcherSearchItem
} from 'landlord/search/models/search-result.model';
import { Store } from '@ngrx/store';
import {
  FetchSearchResults,
  SearchPropertySearcherState
} from 'landlord/search/+state';
import {
  getPagination,
  getSearchActionState,
  getSearchData
} from 'landlord/search/+state/search/search.selectors';
import { Pagination, PropertySearchersTypes } from '@ui/shared/models';
import { Go } from '@ui/legacy-lib';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgbHighlight, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { AsyncPipe, SlicePipe } from '@angular/common';
import { LoadingSpinnerComponent } from '@ui/legacy-lib';
import { AddressPipe } from '@ui/legacy-lib';

@Component({
  selector: 'app-prospect-search',
  templateUrl: './prospect-search.component.html',
  styleUrls: ['./prospect-search.component.scss'],
  imports: [
    FormsModule,
    NgbTypeahead,
    TranslateModule,
    SvgIconComponent,
    AsyncPipe,
    LoadingSpinnerComponent,
    NgbHighlight,
    SlicePipe,
    AddressPipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProspectSearchComponent implements OnInit {
  private store = inject(Store<SearchPropertySearcherState>);
  private router = inject(Router);
  public searchData$: Observable<PropertySearcherSearchBean[]>;
  public pagination$: Observable<Pagination>;
  public isSearching$: Observable<boolean>;
  public focus$ = new Subject<string>();
  public searchDataMap = new Map<string, boolean>();
  public model: string;

  public search = (text$: Observable<string>) =>
    merge(text$, this.focus$).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      map(term => {
        this.searchDataMap.clear();
        if (term.length < 3) return [];
        this.store.dispatch(new FetchSearchResults(term));
        return term;
      }),
      switchMap(() => this.searchData$)
    );

  public formatter = (result: PropertySearcherSearchBean) =>
    `${result.firstname} ${result.name}`;

  public ngOnInit() {
    this.searchData$ = this.store.select(getSearchData);
    this.pagination$ = this.store.select(getPagination);
    this.isSearching$ = this.store
      .select(getSearchActionState)
      .pipe(map(state => state.pending));
  }

  public redirectToFlat(item: PropertySearcherSearchItem) {
    const { id, propertyId } = item;

    // The url should include /properties/XXXX/prospects
    const proposalListPattern = new RegExp('properties\\/\\d+\\/prospects');
    const isUserOnProposalList = proposalListPattern.test(this.router.url);

    this.store.dispatch(
      new Go({
        path: [
          '',
          {
            outlets: {
              primary: `properties/${propertyId}/prospects`,
              side: [
                'aux',
                'ps',
                PropertySearchersTypes.PROSPECTS,
                id,
                propertyId
              ]
            }
          }
        ],
        extras: {
          queryParamsHandling: isUserOnProposalList ? 'preserve' : ''
        }
      })
    );
  }
}
